import React, { useCallback, useEffect, useState } from 'react';
import {
  Group,
  Content,
  Icon,
  Title,
  FormBlock,
  Profile,
  ProfileView,
  Heading,
  FormContent,
  ProfileContent,
  Text,
  SubBlock,
  Form,
  Item,
  Label,
  InputValue,
  Items,
  TextFormat,
  Endblock,
  AddBlock,
  TextForm,
  Icon1,
  RightSide,
  SkipOption
} from './styles';
import { useHistory } from 'react-router-dom';
import { isEmail, isEmpty } from '../../../helpers/common';
import { createWorkspace, dispatchWorkspaceError } from '../../../services/workspaceService';
import UserPreferenceSingleton from '../../../helpers/userPreferenceSingleton';
import Button from '../../../component/button';
import SVGIcon from '../../../assets/images/svg/SVGIcon';
import { clearPreviousWorkspaceData } from '../../../services/baseServices';
import { ROUTES } from '../../../routes/urls';
import { trackAnalyticActivity } from '../../../services/analyticsService';
import { captureException } from '../../../services/logService';
import AppLogo from '../../../component/appLogo';
import { loadInitWorkspaceDetails } from '../../../services/appService';
import { VIEW_ANALYTICS } from '../../../global/analyticsConstants';
import LayoutWithoutNavbar from '../../../component/layoutWithoutNavbar';
import { BUTTON_TYPE, ICON_SIZE } from '../../../global/constants';
import { rootStore } from '../../../mobx/rootStore';
import { observer } from 'mobx-react-lite';
import Image from '../../../component/image';

interface User {
  email: string;
  name: string;
}

const OnboardingStep2: React.FC = () => {
  // States
  const [loading, setLoading] = useState(false);

  //mobx store variables
  const { workspaceStore } = rootStore;
  const { workspaceInputDetails } = workspaceStore;
  const { users } = workspaceInputDetails;
  // Other variables
  const userDetails = UserPreferenceSingleton.getInstance().getCurrentUser();
  const history = useHistory();
  useEffect(() => {
    trackAnalyticActivity(VIEW_ANALYTICS.ONBOARDING_STEP_2);
  }, []);

  const onClickAdd = useCallback(() => {
    rootStore.workspaceStore.addUserItem({ email: '', name: '' });
  }, []);

  const onRemoveItem = useCallback(
    (index: number) => {
      if (users?.length > 1) {
        rootStore.workspaceStore.deleteUserItem(index);
      } else {
        const item = {
          email: '',
          name: ''
        };
        rootStore.workspaceStore.updateUserItem(item, index);
      }
    },
    [users?.length]
  );

  const onChangeInput = useCallback((key: keyof User, item: User, value: string, index: number) => {
    item = {
      ...item,
      [key]: value
    };
    rootStore.workspaceStore.updateUserItem(item, index);
  }, []);

  const onSubmit = useCallback(
    async (e: React.SyntheticEvent, isSkip?: boolean) => {
      try {
        setLoading(true);
        e.preventDefault();
        let error: { email?: string } = {};
        await clearPreviousWorkspaceData();
        if (isSkip) {
          rootStore.workspaceStore.clearWorkspaceInputDetail();
          rootStore.workspaceStore.updateWorkspaceInputDetail('users', []);
          const clearData = { email: '', name: '' };
          rootStore.workspaceStore.updateUserItem(clearData, 0);
        }
        for (const item of workspaceInputDetails.users) {
          if (!isEmpty(item?.email) && !isEmail(item?.email) && !isSkip) {
            error.email = `Please enter a valid email address`;
            break;
          }
        }
        if (!isEmpty(error) && error?.email && !isSkip) {
          dispatchWorkspaceError(error?.email);
          setLoading(false);
          return;
        }
        let payload = {
          industry: workspaceInputDetails?.industry?.value,
          teamType: workspaceInputDetails?.teamType?.value,
          name: workspaceInputDetails?.name
        };
        if (!isSkip) {
          payload = {
            ...(workspaceInputDetails || {}),
            ...(payload || {})
          };
        }
        const result = await createWorkspace(payload);
        try {
          if (result) {
            const response = await loadInitWorkspaceDetails(result?.id);
            if (response && response?.length > 0 && response[0]?.id)
              history.push(ROUTES.PROJECT_TASKS.replace(':id', response[0]?.id || ''));
            else history.push(ROUTES.INBOX);
          }
        } catch (e) {
          captureException(e);
        }
      } catch (e) {
        captureException(e);
      } finally {
        setLoading(false);
      }
    },
    [history, workspaceInputDetails]
  );

  return (
    <>
      <LayoutWithoutNavbar>
        <Group className='windows-onboarding-flow'>
          <Content onSubmit={onSubmit}>
            <Icon>
              <AppLogo height='48' />
            </Icon>
            <Title>Just a few quick things to set up your account…</Title>
            <FormContent>
              <FormBlock>
                <Profile>
                  <Icon>
                    <Image imageKey='onBoarding_step1_profile' alt='profile' />
                  </Icon>
                  <ProfileView>
                    <ProfileContent>
                      <Heading>Hello, {userDetails?.given_name}</Heading>
                    </ProfileContent>
                    <Text>Invite Members</Text>
                  </ProfileView>
                </Profile>

                <SubBlock>
                  <Form>
                    <Items>
                      <Label>Email Address</Label>
                      <Label>Full Name ( Optional )</Label>
                    </Items>
                    {users?.map((item: User, index: number) => {
                      return (
                        <Item key={index}>
                          <InputValue
                            key={index}
                            typeof='email'
                            value={item?.email}
                            onChange={(e) => {
                              onChangeInput('email', item, e.target.value, index);
                            }}
                            placeholder='Email'
                          />
                          <InputValue
                            typeof='text'
                            value={item?.name}
                            onChange={(e) => {
                              onChangeInput('name', item, e.target.value, index);
                            }}
                            placeholder='Full Name'
                          />
                          <Icon
                            role='button'
                            onClick={() => {
                              onRemoveItem(index);
                            }}>
                            <SVGIcon
                              name='round-close-icon'
                              width={ICON_SIZE.LG}
                              height={ICON_SIZE.LG}
                              viewBox={`0 0 ${ICON_SIZE.LG} ${ICON_SIZE.LG}`}
                              className='round-close-icon'
                            />
                          </Icon>
                        </Item>
                      );
                    })}
                  </Form>
                  <AddBlock>
                    <Icon1 role='button' onClick={onClickAdd}>
                      <SVGIcon
                        name='round-plus-icon'
                        width={ICON_SIZE.LG}
                        height={ICON_SIZE.LG}
                        viewBox={`0 0 ${ICON_SIZE.LG} ${ICON_SIZE.LG}`}
                      />
                    </Icon1>
                    <TextForm>
                      <span role='button' onClick={onClickAdd}>
                        Add another
                      </span>
                    </TextForm>
                  </AddBlock>
                </SubBlock>
              </FormBlock>
              <Endblock>
                <TextFormat>Step 2 to 2</TextFormat>
                <RightSide>
                  <SkipOption role='button' disable={loading} onClick={(e: React.SyntheticEvent) => onSubmit(e, true)}>
                    Skip
                  </SkipOption>
                  <Button title='Get Started' variant={BUTTON_TYPE.PRIMARY} buttonType='submit' isLoading={loading} />
                </RightSide>
              </Endblock>
            </FormContent>
          </Content>
        </Group>
      </LayoutWithoutNavbar>
    </>
  );
};
export default observer(OnboardingStep2);
